import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { Add as AddIcon, Refresh as RefreshIcon } from "@material-ui/icons";

import {
    createNewToolboxMapping,
    updateToolboxMapping,
    deleteToolboxMapping,
    getToolboxMappings,
    createCodeMapping,
    updateCodeMapping,
    deleteCodeMapping,
    getCodeMappings,
} from "views/APIs/ToolboxUtility";

import ToolboxFormDialog from "./components/ToolboxFormDialog";
import CodeFormDialog from "./components/CodeFormDialog";
import ToolboxesTable from "./components/ToolboxesTable";
import CodesTable from "./components/CodesTable";
import NotificationContext from "views/Components/Context.js";
import WaveTitle from "../SolutionGenerator/WaveTitle";

export default function CodesDashboard() {
    const theme = useTheme();
    const full = useMediaQuery(theme.breakpoints.down("sm"));

    const { showNotification } = React.useContext(NotificationContext);

    // Lists
    const [toolboxes, setToolboxes] = useState([]);
    const [codes, setCodes] = useState([]);

    const [loadingToolboxes, setLoadingToolboxes] = useState(false);
    const [loadingCodes, setLoadingCodes] = useState(false);

    // UI State
    const [tab, setTab] = useState(0);
    const [toolboxDialogOpen, setToolboxDialogOpen] = useState(false);
    const [codeDialogOpen, setCodeDialogOpen] = useState(false);
    const [editingToolbox, setEditingToolbox] = useState(null);
    const [editingCode, setEditingCode] = useState(null);
    const [saving, setSaving] = useState(false);

    const byToolboxId = useMemo(
        () => Object.fromEntries(toolboxes.map((t) => [t._id, t])),
        [toolboxes]
    );

    // Fetchers
    const refreshToolboxes = async () => {
        setLoadingToolboxes(true);
        try {
            const res = await getToolboxMappings();
            setToolboxes(Array.isArray(res) ? res : res?.data || []);
        } catch (e) {
            showNotification(`Failed to load toolboxes: ${e?.message || e}`, "error");
        } finally {
            setLoadingToolboxes(false);
        }
    };

    const refreshCodes = async () => {
        setLoadingCodes(true);
        try {
            const res = await getCodeMappings();
            setCodes(Array.isArray(res) ? res : res?.data || []);
        } catch (e) {
            showNotification(`Failed to load codes: ${e?.message || e}`, "error");
        } finally {
            setLoadingCodes(false);
        }
    };

    useEffect(() => {
        refreshToolboxes();
        refreshCodes();
    }, []);

    // CRUD Handlers: Toolbox
    const handleSaveToolbox = async (formData) => {
        setSaving(true);
        try {
            
            if (formData._id) {
                const formDataProcessed = new FormData();
                formDataProcessed.append("toolbox_id", formData._id);
                formDataProcessed.append("toolbox_name", formData.name);
                formDataProcessed.append("toolbox_description", formData.description);
                formDataProcessed.append("components", formData.components);
                await updateToolboxMapping(formDataProcessed);
                showNotification("Toolbox updated", "success");
            } else {
                const formDataProcessed = new FormData();
                formDataProcessed.append("toolbox_name", formData.name);
                formDataProcessed.append("toolbox_description", formData.description);
                formDataProcessed.append("components", formData.components);
                await createNewToolboxMapping(formDataProcessed);
                showNotification("Toolbox created", "success");
            }
            setToolboxDialogOpen(false);
            setEditingToolbox(null);
            await refreshToolboxes();
        } catch (e) {
            showNotification(`Save failed: ${e?.message || e}`, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteToolbox = async (row) => {
        if (!window.confirm(`Delete toolbox "${row.toolbox_name}"? This cannot be undone.`)) return;
        try {
            const formData = new FormData();
            formData.append("toolbox_id", row._id);
            await deleteToolboxMapping(formData);
            showNotification("Toolbox deleted", "success");
            await refreshToolboxes();
        } catch (e) {
            showNotification(`Delete failed: ${e?.message || e}`, "error");
        }
    };

    // CRUD Handlers: Code
    const handleSaveCode = async (formData) => {
        setSaving(true);
        try {
            if (formData._id) {
                const formDataProcessed = new FormData();
                formDataProcessed.append("code_id", formData._id);
                formDataProcessed.append("name", formData.name);
                formDataProcessed.append("description", formData.description);
                formDataProcessed.append("code", formData.code);
                formDataProcessed.append("toolboxes_enabled_ids", formData.toolboxes_enabled_ids);
                await updateCodeMapping(formDataProcessed);
                showNotification("Code mapping updated", "success");
            } else {
                const formDataProcessed = new FormData();
                formDataProcessed.append("name", formData.name);
                formDataProcessed.append("description", formData.description);
                formDataProcessed.append("code", formData.code);
                formDataProcessed.append("toolboxes_enabled_ids", formData.toolboxes_enabled_ids);
                await createCodeMapping(formDataProcessed);
                showNotification("Code mapping created", "success");
            }
            setCodeDialogOpen(false);
            setEditingCode(null);
            await refreshCodes();
        } catch (e) {
            showNotification(`Save failed: ${e?.message || e}`, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCode = async (row) => {
        if (!window.confirm(`Delete code mapping "${row.name}"? This cannot be undone.`)) return;
        try {
            const formData = new FormData();
            formData.append("code_id", row._id);
            await deleteCodeMapping(formData);
            showNotification("Code mapping deleted", "success");
            await refreshCodes();
        } catch (e) {
            showNotification(`Delete failed: ${e?.message || e}`, "error");
        }
    };

    return (
        <Box>
            <Divider style={{ marginBottom: "1rem" }} />
            <Typography variant="h4">
                <WaveTitle text="Codes Dashboard" />
            </Typography>
            <Typography variant="body1">
                Here you can manage your codes and toolboxes that the user can gain access to.
            </Typography>
            <Divider style={{ marginTop: "1rem", marginBottom: "1rem" }} />

            <Paper variant="outlined">
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant={full ? "fullWidth" : "standard"}
                >
                    <Tab label="Toolboxes" />
                    <Tab label="Codes" />
                </Tabs>
            </Paper>

            <Box mt={2} display="grid" gridGap={16}>
                {tab === 0 && (
                    <ToolboxesTable
                        toolboxes={toolboxes}
                        loading={loadingToolboxes}
                        onRefresh={refreshToolboxes}
                        onNew={() => {
                            setEditingToolbox(null);
                            setToolboxDialogOpen(true);
                        }}
                        onEdit={(tb) => {
                            setEditingToolbox(tb);
                            setToolboxDialogOpen(true);
                        }}
                        onDelete={handleDeleteToolbox}
                        TopRightActions={
                            <Tooltip title="Refresh">
                                <span>
                                    <IconButton onClick={refreshToolboxes} disabled={loadingToolboxes}>
                                        {loadingToolboxes ? <CircularProgress size={20} /> : <RefreshIcon />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        }
                        NewButton={
                            <Button
                                color="quCreateColor"
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setEditingToolbox(null);
                                    setToolboxDialogOpen(true);
                                }}
                            >
                                New Toolbox
                            </Button>
                        }
                    />
                )}

                {tab === 1 && (
                    <CodesTable
                        codes={codes}
                        byToolboxId={byToolboxId}
                        loading={loadingCodes}
                        onRefresh={refreshCodes}
                        onNew={() => {
                            setEditingCode(null);
                            setCodeDialogOpen(true);
                        }}
                        onEdit={(c) => {
                            setEditingCode(c);
                            setCodeDialogOpen(true);
                        }}
                        onDelete={handleDeleteCode}
                        TopRightActions={
                            <Tooltip title="Refresh">
                                <span>
                                    <IconButton onClick={refreshCodes} disabled={loadingCodes}>
                                        {loadingCodes ? <CircularProgress size={20} /> : <RefreshIcon />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        }
                        NewButton={
                            <Button
                                color="quCreateColor"
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setEditingCode(null);
                                    setCodeDialogOpen(true);
                                }}
                            >
                                New Code
                            </Button>
                        }
                    />
                )}
            </Box>

            {/* Dialogs */}
            <ToolboxFormDialog
                open={toolboxDialogOpen}
                onClose={() => setToolboxDialogOpen(false)}
                initial={editingToolbox}
                onSave={handleSaveToolbox}
                saving={saving}
            />

            <CodeFormDialog
                open={codeDialogOpen}
                onClose={() => setCodeDialogOpen(false)}
                initial={editingCode}
                onSave={handleSaveCode}
                saving={saving}
                toolboxes={toolboxes}
            />


        </Box>
    );
}
